// First-party page-view beacon for fly-social.com.
//
// Mounted once inside the Router. On every public marketing-page load it sends
// a lightweight beacon to capture-site-view so the UTM Traffic cockpit can show
// where fly-social.com's OWN traffic comes from (Google, social, direct,
// campaigns) — the piece agency_embed_views never covered.
//
// Skips:
//   • the authed app (/fly-app) — it has its own analytics. ONE exception,
//     added 16 Sep 2026: a /fly-app arrival that carries utm_* params is a
//     campaign click (lifecycle and win-back emails link straight into the
//     tools page so a signed-in owner lands in the product, not on marketing
//     copy). Those are recorded, so the UTM Traffic cockpit can attribute them
//     to the saved link. Untagged in-app navigation stays out, as before.
//   • agency embeds (/embed) — covered by agency_embed_views,
//   • white-label agency domains (__AGENCY_BRANDING__) — not fly-social.com.
//
// A private agent page (an owner link /agent/<page>/<token>, the signed-in
// agent app /app/l/<location>, the agent's booking or checkout link:
// src/lib/privatePaths.ts) is recorded as its route template,
// /agent/home/:token, never with the token. A referrer that is one of our own
// private pages (this host or its www twin) is masked the same way. Every other
// path and referrer, the GMB tool's included, goes out exactly as before.
//
// Deduped once per (path) per tab session so re-renders never double-count. The
// first beacon of a session is flagged is_entry — its referrer is the real
// external source (later in-session navigations carry an internal referrer).

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { captureUtmFromUrl } from '@/lib/utmTracking';
import { maskPrivatePath, scrubUrl } from '@/lib/privatePaths';
import { getSiteSessionId } from '@/lib/siteSession';

const FIRED_KEY = 'fly_site_views_fired_v1';
const ENTRY_KEY = 'fly_site_entry_done_v1';

declare global {
  interface Window { __AGENCY_BRANDING__?: boolean }
}

function readFired(): Set<string> {
  try { return new Set(JSON.parse(sessionStorage.getItem(FIRED_KEY) || '[]') as string[]); }
  catch { return new Set(); }
}

export function SiteTrafficBeacon() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (pathname.startsWith('/embed')) return;
    if (window.__AGENCY_BRANDING__) return;

    const isEntry = !sessionStorage.getItem(ENTRY_KEY);
    const utm = captureUtmFromUrl();

    // The authed app is skipped UNLESS this very navigation carries utm_* in
    // the LIVE URL. Deliberately not `utm.utm_source`: captureUtmFromUrl falls
    // back to the session-stored first-touch bag, which stays set for the whole
    // session after one tagged click, and would have recorded every in-app page
    // from then on. Email CTAs deep-link into /fly-app/lead-gen-tools; without
    // this exception those clicks would never appear in the UTM Traffic cockpit.
    // Untagged in-app navigation stays out, as before.
    if (pathname.startsWith('/fly-app')) {
      const live = new URLSearchParams(window.location.search);
      if (!live.get('utm_source')) return;
    }

    // Dedupe per (path + campaign tags), NOT path alone. A path-only key meant a
    // UTM-tagged visit was skipped whenever the bare path had already been seen
    // this session (e.g. the visitor browsed fly-social.com first, then clicked
    // a tagged link to the same page) — so that campaign click never recorded.
    // Including the tags makes a tagged visit distinct, so it always lands.
    const path = maskPrivatePath(pathname);
    const utmSig = [utm.utm_source, utm.utm_medium, utm.utm_campaign].filter(Boolean).join('|');
    const dedupeKey = utmSig ? `${path}?${utmSig}` : path;

    const fired = readFired();
    if (fired.has(dedupeKey)) return;

    void supabase.functions.invoke('capture-site-view', {
      body: {
        path,
        referrer: document.referrer ? scrubUrl(document.referrer, { ownHost: window.location.host }) : null,
        is_entry: isEntry,
        utm_source: utm.utm_source ?? null,
        utm_medium: utm.utm_medium ?? null,
        utm_campaign: utm.utm_campaign ?? null,
        utm_term: utm.utm_term ?? null,
        utm_content: utm.utm_content ?? null,
        session_id: getSiteSessionId(),
      },
    }).catch(() => { /* best-effort telemetry; never block the page */ });

    fired.add(dedupeKey);
    try { sessionStorage.setItem(FIRED_KEY, JSON.stringify([...fired])); } catch { /* quota */ }
    try { sessionStorage.setItem(ENTRY_KEY, '1'); } catch { /* quota */ }
  }, [pathname]);

  return null;
}
